import { createContext, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import getGqlClient from '@spotahome/ui-library/src/tenant/clients/graphql/marketplace';

import { getPricesCityStatistics } from '@spotahome/marketplace-common/src/graphql/queries/searchStatistics';
import { clientErrorTracker } from '@spotahome/soyuz/client';
import {
  formatMonthAndYearAbbrvDate,
  formatDate
} from '@spotahome/ui-library/src/tenant/components/utils/dateUtils';

import { capitalizeFirstLetter } from '@spotahome/ui-library/src/tenant/components/utils/stringUtils';

import { getQueryPropertyType } from '../utils/seoCharts';

const DECIMAL_DIGITS = 1;

const defaultFn = () => {};

const normalizeData = data => {
  return data?.map(item => {
    const fullDate = new Date();
    fullDate.setMonth(item.month - 1);
    fullDate.setFullYear(item.year);
    return {
      ...item,
      label: capitalizeFirstLetter(formatMonthAndYearAbbrvDate(fullDate)),
      monthName: formatDate(fullDate, 'MMMM'),
      averagePrice: parseFloat(item.averagePrice / 100).toFixed(DECIMAL_DIGITS)
    };
  });
};

const PricesStatisticsContext = createContext({
  pricesStatistics: [],
  loading: false,
  setPropertyType: defaultFn,
  lastMonthDisplayed: {},
  deltaAverage: null,
  priceAverage: null,
  currencyIsoCode: null
});

export const PricesStatisticsProvider = ({
  children,
  cityId,
  propertyType: initialPropertyType,
  pricesStatistics: initialPricesStatistics = [],
  currencyIsoCode
}) => {
  const allStats = normalizeData(initialPricesStatistics);

  const [loading, setLoading] = useState(false);
  const [propertyType, setInternalPropertyType] = useState(initialPropertyType);
  const [pricesStatistics, setPricesStatistics] = useState(allStats);

  const requestPricesStatistics = async () => {
    setLoading(true);
    try {
      const response = await getPricesCityStatistics(getGqlClient())({
        cityId,
        propertyType: getQueryPropertyType(propertyType)
      });

      setPricesStatistics(
        normalizeData(response?.data?.cityPricesStatistics ?? [])
      );
    } catch (error) {
      setPricesStatistics([]);
      clientErrorTracker.error({
        error,
        source: 'PricesStatisticsProvider-requestPricesStatistics'
      });
    }
    setLoading(false);
  };

  const getStats = () =>
    pricesStatistics.length > 0 ? pricesStatistics : allStats;

  const getPriceAverage = () => {
    const stats = getStats();
    const total = stats.reduce(
      (acc, item) => acc + parseFloat(item.averagePrice),
      0
    );
    return parseFloat(total / stats.length).toFixed(DECIMAL_DIGITS);
  };

  const getLastMonthDisplayed = () => {
    const stats = getStats();
    return stats[stats.length - 1];
  };

  useEffect(() => {
    requestPricesStatistics();
  }, [propertyType]);

  const setPropertyType = newPropertyType => {
    if (newPropertyType !== propertyType) {
      setInternalPropertyType(newPropertyType);
    }
  };

  return (
    <PricesStatisticsContext.Provider
      value={{
        cityId,
        initialPropertyType,
        propertyType,
        pricesStatistics,
        setPropertyType,
        priceAverage: getPriceAverage(),
        lastMonthDisplayed: getLastMonthDisplayed(),
        currencyIsoCode,
        loading
      }}
    >
      {children}
    </PricesStatisticsContext.Provider>
  );
};

PricesStatisticsProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired,
  cityId: PropTypes.string.isRequired,
  propertyType: PropTypes.string.isRequired,
  pricesStatistics: PropTypes.arrayOf(
    PropTypes.shape({
      month: PropTypes.string,
      year: PropTypes.string,
      averagePrice: PropTypes.number,
      delta: PropTypes.number
    })
  ),
  currencyIsoCode: PropTypes.string.isRequired
};

export const usePricesStatisticsContext = () =>
  useContext(PricesStatisticsContext);

export default {
  PricesStatisticsProvider,
  usePricesStatisticsContext
};
